import React from 'react'
import { useTranslation } from 'react-i18next'

import {
  CheckoutRoute,
  useCheckoutRouter,
} from '@src/hooks/useCheckoutRouter/useCheckoutRouter'
import { UseBasketTotalsResponseData } from '@src/hooks/useTotals/useBasketTotals'
import { useBasketTotalsContext } from '@src/hooks/useTotals/BasketTotalsContext'

import {
  LoyaltyCardAmountBold,
  LoyaltyCardImage,
  LoyaltyCardText,
} from '../CustomerLoyaltyCard.styles'

import { outletFulfilment_outlet_outletLoyaltyCards } from '@src/hooks/outletFulfilmentAndBasketHooks/useOutletFulfilmentData/queries/__generated__/outletFulfilment'
import {
  EarningContainer,
  IconTextAndDetailsContainer,
  RewardNameContainer,
  ExtraInfoAndAmountEarnedContainer,
  TextAndIconContainer,
} from './CheckoutRewards.styles'
import { ScallopedMaskSVGLarge } from '@src/components/SVGS/ScallopedMaskSVGLarge'
import { imageJitURL } from '@src/utils/imageJitURL'
import { useFormatCurrency } from '@src/hooks/useFormatCurrency'
import { useReactiveVar } from '@apollo/client'
import { paymentMethodVar } from '@src/hooks/useTotals/utils/paymentMethodReactiveVar'
import { DetailedPaymentMethod } from '@src/graphql-types'

import { LoyaltyCardStampProgressMeter } from '../StampProgressMeter'
import { calculatePotentialToEarnOnOrder } from '@src/components/CustomerLoyaltyCard/CheckoutRewards/calculatePotentialToEarnForLoyalty'
import { TextClamp } from '@src/components/Text/Clamp'

export const filterLoyaltyCards = (
  cards: outletFulfilment_outlet_outletLoyaltyCards[],
  basketTotals?: UseBasketTotalsResponseData | null
) =>
  cards.filter(card => {
    const canEarnStamp = card.isAvailableToday
    const isCardIncomplete =
      (card.loyaltyCardStamps?.length ?? 0) < card.requiredStamps

    const meetsBasketCriteria =
      !basketTotals || card.amountToSpendToEarnAStamp === 0

    return (meetsBasketCriteria && canEarnStamp) || !isCardIncomplete
  })

export const LoyaltyCardDealsSection: React.FC<{
  cardsData: outletFulfilment_outlet_outletLoyaltyCards[]
}> = ({ cardsData }) => {
  const { t } = useTranslation('customerLoyaltyCard')
  const { basketTotals } = useBasketTotalsContext()

  const checkout = useCheckoutRouter()
  const paymentMethod = useReactiveVar(paymentMethodVar)
  const formatCurrency = useFormatCurrency()

  if (!cardsData.length) {
    return null
  }

  // filter out the ones that don't reach the minimum required total to earn a stamp, except if they are full
  // and full ones if the flag is false
  const loyaltyCardsMinimumCheck =
    checkout.route === CheckoutRoute.BASKET || checkout.route === undefined
      ? filterLoyaltyCards(cardsData)
      : filterLoyaltyCards(cardsData, basketTotals.data ?? undefined)

  // extra check to ensure redeemable cards are removed when a discount code is applied
  const loyaltyCards = loyaltyCardsMinimumCheck.filter(card => {
    const hasVoucherDiscount =
      !!basketTotals?.data?.checkoutBasketTotals?.appliedDiscount?.discount
        ?.voucher
    const isFull = card.loyaltyCardStamps.length === card.requiredStamps
    if (isFull && hasVoucherDiscount) return false

    if (
      isFull &&
      card.id !==
        basketTotals?.data?.checkoutBasketTotals?.appliedDiscount?.discount
          ?.loyaltyCard?.id
    )
      return false
    return true
  })

  return (
    <>
      {loyaltyCards.length > 0 &&
        loyaltyCards.map((loyaltyCard, index) => {
          const amountToSpend =
            loyaltyCard.loyaltyCardStamps.length < loyaltyCard.requiredStamps
              ? loyaltyCard.amountToSpendToEarnAStamp ?? 0
              : 0

          const potentialToEarn = calculatePotentialToEarnOnOrder(
            basketTotals.data || undefined,
            loyaltyCard
          )

          const appliedDiscount =
            basketTotals?.data?.checkoutBasketTotals?.appliedDiscount
          const redeemAmount = appliedDiscount?.discount?.loyaltyCard
            ? appliedDiscount.reductionAmount
            : 0

          const preventCashOrders =
            !loyaltyCard.allowCashOrders &&
            checkout.route === CheckoutRoute.PAYMENT &&
            paymentMethod.detailedPaymentMethod === DetailedPaymentMethod.CASH

          const isRedeeming =
            loyaltyCard.loyaltyCardStamps.length === loyaltyCard.requiredStamps

          return (
            <IconTextAndDetailsContainer
              key={`${loyaltyCard.discount.name}-${index}`}
            >
              <TextAndIconContainer>
                <ScallopedMaskSVGLarge scale={0.32} />
                <LoyaltyCardImage
                  isCheckout={true}
                  canStampBeEarned={true}
                  role="img"
                  aria-label={loyaltyCard.discount.id}
                  imageUrl={imageJitURL(loyaltyCard.loyaltyCardIcon, {
                    resize: {
                      width: 180,
                      height: 180,
                      fit: 'cover',
                    },
                  })}
                  scale={0.32}
                />
                <RewardNameContainer color={loyaltyCard.loyaltyCardColor}>
                  <TextClamp
                    tooltipText={loyaltyCard.discount.name}
                    data-test-id="loyalty-card-text-discountname"
                  >
                    {loyaltyCard.discount.name}
                  </TextClamp>
                </RewardNameContainer>
              </TextAndIconContainer>
              <ExtraInfoAndAmountEarnedContainer>
                {preventCashOrders ? (
                  <EarningContainer>
                    <LoyaltyCardText
                      isTermsPage={false}
                      isBasket={true}
                      data-test-id="loyalty-card-text-earningredeeming"
                    >
                      {loyaltyCard.loyaltyCardStamps.length ===
                      loyaltyCard.requiredStamps ? (
                        <>{t('pay_by_card_to_redeem')}</>
                      ) : (
                        <>{t('pay_by_card_to_earn')}</>
                      )}
                    </LoyaltyCardText>
                  </EarningContainer>
                ) : amountToSpend > 0 ? (
                  <EarningContainer>
                    <LoyaltyCardText
                      isBasket={true}
                      isTermsPage={false}
                      data-test-id="loyalty-card-text-earningredeeming"
                    >
                      <>
                        {t('add')}
                        <LoyaltyCardAmountBold isBasket={true}>
                          {' '}
                          {formatCurrency(amountToSpend)}{' '}
                        </LoyaltyCardAmountBold>
                        {t('more_to_earn_a_stamp_short')}
                      </>
                    </LoyaltyCardText>
                  </EarningContainer>
                ) : preventCashOrders ? (
                  <LoyaltyCardText
                    isTermsPage={false}
                    isBasket={true}
                    data-test-id="loyalty-card-text-earningredeeming"
                  >
                    {t('cash_orders_not_accepted')}{' '}
                  </LoyaltyCardText>
                ) : (
                  <EarningContainer
                    data-test-id={`loyalty-card-earningcontainer-${
                      isRedeeming ? 'redeem' : 'earning'
                    }`}
                  >
                    {isRedeeming ? (
                      <>
                        {t('redeem')}{' '}
                        <LoyaltyCardAmountBold isBasket={true}>
                          {formatCurrency(redeemAmount)}
                        </LoyaltyCardAmountBold>
                      </>
                    ) : (
                      <>
                        {t('earn')}{' '}
                        <LoyaltyCardAmountBold isBasket={true}>
                          {formatCurrency(potentialToEarn)}
                        </LoyaltyCardAmountBold>
                      </>
                    )}
                  </EarningContainer>
                )}
                {!preventCashOrders && (
                  <LoyaltyCardStampProgressMeter
                    loyaltyCard={loyaltyCard}
                    isBasket={true}
                  />
                )}
              </ExtraInfoAndAmountEarnedContainer>
            </IconTextAndDetailsContainer>
          )
        })}
    </>
  )
}
